<template>
    <b-modal id="campaign-rule-set-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             @hide="onHide"
             @hidden="onHidden"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <template v-else-if="mode === 'detail'">
                <b-button variant="outline-primary"
                          :disabled="!detailData.id"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button variant="outline-primary"
                          form="campaign-rule-set-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button variant="outline-danger"
                          :disabled="deleting"
                          @click="confirmDelete"
                >
                    <b-spinner v-if="deleting" small/>
                    {{ $t('삭제') }}
                </b-button>
                <b-button variant="outline-primary"
                          form="campaign-rule-set-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          @click="cancelEdit"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <!-- 이 규칙셋이 속한 프로젝트로 지금 평가 신청이 가능한지. 목록의 "신청 가능"
             배지와 같은 판정이며, 여기서는 왜 불가능한지(빠진 입력키)까지 보여준다. -->
        <b-alert
            v-if="mode !== 'add' && readiness"
            :variant="readiness.ready ? 'success' : 'danger'"
            show
            class="mb-3"
        >
            <template v-if="readiness.ready">
                {{ $t('이 프로젝트로 평가 신청이 가능합니다.') }}
            </template>
            <template v-else>
                <strong>{{ $t('이 프로젝트로는 현재 평가 신청이 불가능합니다.') }}</strong>
                <div v-if="readiness.reason === 'missing_input_keys'" class="mt-2">
                    {{ $t('규칙이 참조하는 입력키가 이 프로젝트에 등록되지 않았습니다') }}:
                    <b-badge v-for="k in readiness.missing_keys" :key="k" variant="light" class="mr-1">{{ k }}</b-badge>
                    <div class="mt-1 small">
                        {{ $t('[프로젝트 스키마 관리 > 스키마 입력 값 관리] 에서 위 키를 등록한 뒤 다시 시도하세요.') }}
                    </div>
                </div>
                <div v-else-if="readiness.reason === 'no_campaign_mapping'" class="mt-2 small">
                    {{ $t('활성 규칙셋도 없고 프로젝트에 캠페인도 연결되어 있지 않습니다.') }}
                </div>
                <div v-else-if="readiness.reason === 'no_required_keys'" class="mt-2 small">
                    {{ $t('규칙셋에 조건이 하나도 없어 캠페인을 판별할 수 없습니다.') }}
                </div>
            </template>
        </b-alert>
        <div class="tabs-container">
            <b-tabs v-model="tabIndex" class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <PageEmptyComponent v-else-if="loadError && mode !== 'add'" compact :error="loadError" @retry="fetchDetailData"/>
                    <template v-else>
                        <campaign-rule-set-detail v-if="mode === 'detail' || mode ==='readOnly'"
                                                  :detail-data="detailData"
                                                  :campaigns="campaigns"
                                                  :projects="projects"
                        />
                        <campaign-rule-set-form v-else ref="campaignRuleSetForm" :mode="mode" :detail-data="detailData"
                                                :campaigns="campaigns"
                                                :projects="projects"
                                                @updateDetailData="updateDetailData"
                                                @dirty="v => formDirty = v"
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {deleteCampaignRuleSet, getCampaignRuleSet, listCampaignRuleSets} from '@shared_utils/api/ruleEngine';
import {getCampaignReadiness} from '@shared_utils/api/projectSchema';
import {getTargetCampaignText, getTargetProjectText} from '@shared/components/admin/pages/Management/common-data';

import CampaignRuleSetDetail
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetDetail.vue';
import CampaignRuleSetForm
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetForm.vue';
import unsavedChangesGuard from '@shared/mixin/unsaved-changes-guard';

const CLOSE_CONFIRM = {title: '저장하지 않은 입력', message: '입력한 내용이 저장되지 않았습니다. 닫을까요?'};
const EDIT_CANCEL_CONFIRM = {title: '저장하지 않은 입력', message: '수정한 내용이 저장되지 않았습니다. 수정을 취소할까요?'};

export default {
    name: 'CampaignRuleSetModal',
    components: {
        PageEmptyComponent,
        CampaignRuleSetForm,
        CampaignRuleSetDetail,
    },
    mixins: [unsavedChangesGuard],
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        },
        campaigns: {
            type: [Array, null],
            default: null,
        },
        projects: {
            type: [Array, null],
            default: null,
        }
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            tabIndex: 1,
            detailData: {id: null},
            loading: false,
            loadError: '',
            // 이 규칙셋 프로젝트의 신청 가능 상태 (campaign-readiness 응답).
            readiness: null,
            // 다른 규칙으로 바꾼 뒤 늦게 온 이전 상세 응답을 버리기 위한 요청 순번
            detailSeq: 0,
            deleting: false,
            formDirty: false,
            confirmingClose: false,
        };
    },
    computed: {
        // 등록·수정 폼에서 값이 바뀌었는지 (폼이 @dirty 로 알려 준다)
        hasUnsavedChanges() {
            return (this.mode === 'add' || this.mode === 'edit') && this.formDirty;
        },
        modalTitle() {
            if (this.mode === 'add') {
                return this.$t('캠페인 규칙셋 등록');
            } else if (this.mode === 'edit') {
                return this.$t('캠페인 규칙셋 수정');
            } else {
                return this.$t('캠페인 규칙셋 상세');
            }
        },
        readOnly() {
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.detailSeq += 1;
                    this.loading = false;
                    this.loadError = '';
                    this.detailData = {id: null};
                    this.readiness = null;
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            this.mode = mode;
            this.tabIndex = 1;
        },
        async cancelEdit() {
            if (!(await this.confirmDiscardChanges(EDIT_CANCEL_CONFIRM))) return;
            this.changeMode('detail');
        },
        // X·ESC·바깥 클릭·hide() 모두 이리로 온다. 입력이 바뀌었으면 확인 후 닫는다(화면 정리는 hidden 에서)
        onHide(bvEvent) {
            if (!this.hasUnsavedChanges && !this.confirmingClose) return;
            bvEvent.preventDefault();
            if (this.confirmingClose) return;
            this.confirmingClose = true;
            this.confirmDiscardChanges(CLOSE_CONFIRM).then((ok) => {
                this.confirmingClose = false;
                if (ok) this.closeModal();
            });
        },
        onHidden() {
            this.mode = 'detail';
        },
        // 저장·삭제 뒤처럼 코드에서 닫을 때는 미저장 확인을 묻지 않는다
        closeModal() {
            this.formDirty = false;
            this.$bvModal.hide('campaign-rule-set-detail-modal');
        },
        async fetchDetailData() {
            const seq = ++this.detailSeq;
            try {
                this.loading = true;
                this.loadError = '';
                this.detailData = {id: null};
                this.readiness = null;
                const res = await getCampaignRuleSet(this.detailId);
                if (seq !== this.detailSeq) return;
                this.detailData = res.data;
                this.fetchReadiness(seq);
            } catch (e) {
                if (seq !== this.detailSeq) return;
                console.error(e);
                if (e?.response?.status === 401) return;
                this.loadError = e.response && e.response.status === 404
                    ? '존재하지 않는 항목입니다.'
                    : getErrorMessage(e, '상세 정보를 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', this.$t('상세 조회 실패'), this.loadError);
            } finally {
                if (seq === this.detailSeq) this.loading = false;
            }
        },
        async fetchReadiness(seq) {
            this.readiness = null;
            const projectId = this.detailData && this.detailData.project_id;
            if (!projectId) {
                return;
            }
            try {
                const {data} = await getCampaignReadiness(projectId);
                if (seq === this.detailSeq) this.readiness = data;
            } catch (e) {
                // 배너만 생략한다. 상세 조회 자체는 이미 성공했다.
            }
        },
        updateDetailData(detailData) {
            if (this.mode === 'add') {
                this.$emit('addData', detailData);
                this.closeModal();
            } else if (this.mode === 'edit') {
                this.$emit('updateDetailData', detailData);
                this.detailData = detailData;
                this.changeMode('detail');
            }
        },
        // 어떤 규칙인지와, 지운 뒤 이 프로젝트에 남는 활성 규칙 수를 보여 주고 확인받는다(마지막 규칙을 지워 신청이 막히는 사고 방지)
        async confirmDelete() {
            if (this.deleting) return;
            this.deleting = true;
            try {
                const d = this.detailData;
                const lines = [
                    `규칙: ${d.name}`,
                    `프로젝트: ${getTargetProjectText(d.project_id, this.projects) || `#${d.project_id}`}`,
                    `평가지: ${getTargetCampaignText(d.campaign_id, this.campaigns) || `#${d.campaign_id}`}`,
                    `상태: ${d.is_active ? '활성' : '비활성'}`,
                ];
                if (d.is_active) {
                    const left = await this.countActiveAfterDelete(d.project_id);
                    if (left != null) {
                        lines.push(`삭제하면 이 프로젝트의 활성 규칙은 ${left}개가 됩니다.`
                            + (left === 0 ? ' 활성 규칙이 없으면 모든 신청자가 기본 평가지로 배정되고, 기본 평가지가 없으면 신청할 수 없습니다.' : ''));
                    }
                }
                lines.push('삭제하면 되돌릴 수 없습니다.');
                const h = this.$createElement;
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: this.$t('캠페인 규칙셋 삭제'),
                    message: h('div', {style: {whiteSpace: 'pre-line'}}, lines.join('\n'))
                });
                if (ok) await this.deleteRuleSet();
            } finally {
                this.deleting = false;
            }
        },
        // 조회에 실패하면 null — 확인창에서 그 문장만 빠진다
        async countActiveAfterDelete(projectId) {
            try {
                const {data} = await listCampaignRuleSets({params: {project_id: projectId, is_active: true, page: 1, per_page: 1}});
                return data && data.total != null ? Math.max(data.total - 1, 0) : null;
            } catch (e) {
                return null;
            }
        },
        deleteRuleSet() {
            return deleteCampaignRuleSet(this.detailData.id)
                .then(() => {
                    this.$EventBus.$emit('toast-success', this.$t('캠페인 규칙셋 삭제'), this.$t('캠페인 규칙셋이 성공적으로 삭제되었습니다.'));
                    this.$emit('deleteData', this.detailData.id);
                    this.closeModal();
                })
                .catch((error) => {
                    console.error(error);
                    if (error?.response?.status === 401) return;
                    this.$EventBus.$emit('toast-failure', this.$t('캠페인 규칙셋 삭제'), getErrorMessage(error, this.$t('캠페인 규칙셋 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.')));
                });
        }
    },
};
</script>

<style scoped lang="scss">

</style>
